import { useCallback, useEffect, useRef } from "react";
import type { UseFormRegisterReturn } from "react-hook-form";

const MAX_ROWS = 30;

interface AutoResizeTextareaProps {
  registration: UseFormRegisterReturn;
  value: string | undefined;
  readOnly?: boolean;
  placeholder?: string;
  className?: string;
}

export default function AutoResizeTextarea({
  registration,
  value,
  readOnly,
  placeholder,
  className,
}: AutoResizeTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { ref, onChange, ...rest } = registration;

  const resize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;

    el.style.removeProperty("height");
    const style = getComputedStyle(el);
    const lineHeight = Number.parseFloat(style.lineHeight) || 20;
    const padding =
      Number.parseFloat(style.paddingTop) +
      Number.parseFloat(style.paddingBottom);
    const border =
      Number.parseFloat(style.borderTopWidth) +
      Number.parseFloat(style.borderBottomWidth);
    const minHeight = lineHeight + padding + border;
    const maxHeight = lineHeight * MAX_ROWS + padding + border;
    const nextHeight = Math.min(
      Math.max(el.scrollHeight, minHeight),
      maxHeight,
    );

    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
  }, []);

  useEffect(() => {
    resize();
  }, [value, resize]);

  const setRef = useCallback(
    (element: HTMLTextAreaElement | null) => {
      ref(element);
      textareaRef.current = element;
      if (element) {
        requestAnimationFrame(resize);
      }
    },
    [ref, resize],
  );

  const handleInput = useCallback(
    (event: React.FormEvent<HTMLTextAreaElement>) => {
      onChange(event);
      requestAnimationFrame(resize);
    },
    [onChange, resize],
  );

  return (
    <textarea
      {...rest}
      ref={setRef}
      rows={1}
      readOnly={readOnly}
      placeholder={placeholder}
      className={className}
      onInput={handleInput}
    />
  );
}
